/**
 * utils/errorHandler.ts
 * Single source of truth for extracting and displaying backend error messages.
 *
 * SK Upsell API error shape : { error: string, message?: string, details?: {...} }
 * Platform API error shape  : { error: { code, message } } | { message } | { title }
 * Network/JS error          : Error.message
 */
import { showToast } from '../components/toastComponent';

const MAX_LENGTH = 300;
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/** Blocks HTML/script injection from ever reaching a toast. */
const BLOCKED = /<|>|javascript:|data:/i;

function isSafeString(val: unknown): val is string {
  return typeof val === 'string' && val.trim().length > 0 && !BLOCKED.test(val);
}

interface ApiErrorShape {
  response?: {
    data?: {
      message?: unknown;
      error?: unknown;
      errors?: Array<{ message?: unknown }>;
      title?: unknown;
    };
  };
  message?: unknown;
}

/** Extracts the most specific backend message; falls back only when none found. */
export function getApiErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!error || typeof error !== 'object') return fallback;
  const err = error as ApiErrorShape;
  const data = err.response?.data;

  // 1. data.message (SK Upsell + most APIs)
  if (isSafeString(data?.message)) return (data!.message as string).trim().slice(0, MAX_LENGTH);

  // 2. data.error.message (platform envelope { error: { code, message } })
  const errObj = data?.error;
  if (errObj && typeof errObj === 'object' && isSafeString((errObj as { message?: unknown }).message)) {
    return ((errObj as { message: string }).message).trim().slice(0, MAX_LENGTH);
  }

  // 3. data.errors[0].message (validation arrays)
  const first = Array.isArray(data?.errors) ? data!.errors[0] : null;
  if (first && isSafeString(first.message)) return (first.message as string).trim().slice(0, MAX_LENGTH);

  // 4. data.title (RFC 7807)
  if (isSafeString(data?.title)) return (data!.title as string).trim().slice(0, MAX_LENGTH);

  // 5. data.error as a short string code (SK Upsell `{ error: 'rule_not_found' }`)
  if (isSafeString(errObj)) return (errObj as string).trim().slice(0, MAX_LENGTH);

  // 6. Network / JS error
  if (isSafeString(err.message)) return (err.message as string).trim().slice(0, MAX_LENGTH);

  return fallback;
}

/** Shows a backend error message in an error toast. Use in every catch block. */
export function handleApiError(error: unknown, title: string, fallback = DEFAULT_FALLBACK): void {
  showToast('error', title, getApiErrorMessage(error, fallback));
}
